import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api-client';
import { MainLayout } from '../components/MainLayout';

export function KycPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // TODO: Make country selection dynamic - for now defaulting to NG
  // In the future, this should be derived from user profile or session context
  const [countryCode] = useState('NG'); // Default to Nigeria for now
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const queryClient = useQueryClient();

  // Get URL parameters from widget redirect
  const sessionId = searchParams.get('sessionId');
  const errorType = searchParams.get('error');
  const errorFields = searchParams.get('fields')?.split(',') || [];

  // Fetch KYC status
  // vendorId is auto-resolved from countryCode by the backend
  const { data: statusData, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['kyc-status', countryCode],
    queryFn: () => api.kyc.status({ countryCode }),
  });

  // Fetch KYC requirements
  const { data: requirementsData, isLoading: isLoadingRequirements } = useQuery({
    queryKey: ['kyc-requirements', countryCode],
    queryFn: () => api.kyc.requirements({ countryCode }),
  });

  // Fetch existing KYC data
  const { data: kycData, isLoading: isLoadingData } = useQuery({
    queryKey: ['kyc-data', countryCode],
    queryFn: () => api.kyc.data({ countryCode }),
    enabled: statusData?.data?.status === 'VERIFIED' && isEditing,
  });

  // Fetch transaction limits
  const { data: limitsData, isLoading: isLoadingLimits } = useQuery({
    queryKey: ['transaction-limits'],
    queryFn: () => api.ramp.getLimits(),
  });

  const status = statusData?.data;
  const requirements = requirementsData?.data;
  const transactionLimits = limitsData?.data;
  const kycStatus = status?.status;

  // Initialize form with existing data
  useEffect(() => {
    if (kycData?.data?.documents) {
      setFormData(kycData.data.documents);
    }
  }, [kycData]);

  // Submit KYC mutation
  // vendorId is auto-resolved from countryCode by the backend
  const submitMutation = useMutation({
    mutationFn: (documents: Record<string, string>) =>
      api.kyc.submit({ countryCode, documents }),
    onSuccess: () => {
      setSubmitSuccess(true);
      setSubmitError(null);
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-data'] });

      // Clear URL parameters after successful update
      if (sessionId) {
        setSearchParams({});
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.error || 'Failed to submit KYC details');
      setSubmitSuccess(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    submitMutation.mutate(formData);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'bg-green-500/10 text-green-400 border-green-500/30';
      case 'PENDING':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
      case 'FAILED':
      case 'EXPIRED':
        return 'bg-red-500/10 text-red-400 border-red-500/30';
      default:
        return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
    }
  };

  const getStatusMessage = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'Your identity has been verified successfully.';
      case 'PENDING':
        return 'Your verification is being reviewed. This usually takes 1-2 business days.';
      case 'FAILED':
        return 'Your verification was not successful. Please review the requirements and try again.';
      case 'EXPIRED':
        return 'Your verification has expired. Please submit new documents.';
      default:
        return 'You have not submitted KYC documents yet. Complete verification to unlock all features.';
    }
  };

  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      FULL_NAME: 'Full Name',
      PHONE: 'Phone Number',
      ADDRESS: 'Address',
      DOB: 'Date of Birth',
      EMAIL: 'Email Address',
      NIN: 'National Identification Number (NIN)',
      BVN: 'Bank Verification Number (BVN)',
      ID_NUMBER: 'ID Number',
      ID_TYPE: 'ID Type'
    };
    return labels[field] || field;
  };

  const getFieldPlaceholder = (field: string): string => {
    const placeholders: Record<string, string> = {
      FULL_NAME: 'John Doe',
      PHONE: '+2348012345678',
      ADDRESS: '123 Main Street, Lagos, Nigeria',
      DOB: 'YYYY-MM-DD',
      EMAIL: 'john.doe@example.com',
      NIN: '12345678901',
      BVN: '12345678901',
      ID_NUMBER: 'ID123456',
      ID_TYPE: 'Passport, National ID, Driver License'
    };
    return placeholders[field] || '';
  };

  const getFieldType = (field: string): string => {
    if (field === 'EMAIL') return 'email';
    if (field === 'DOB') return 'date';
    if (field === 'PHONE') return 'tel';
    return 'text';
  };

  const isLoading = isLoadingStatus || isLoadingRequirements || (isEditing && isLoadingData);

  return (
    <MainLayout>
      <div className="space-y-[var(--space-2xl)] max-w-3xl">
        <div>
          <h1 className="text-[var(--font-size-display-m)] md:text-[var(--font-size-display-l)] font-[var(--font-weight-bold)] text-[var(--fg-1)] font-[var(--font-display)]">
            KYC Verification
          </h1>
          <p className="text-[var(--font-size-body)] text-[var(--fg-2)] mt-[var(--space-s)]">
            Verify your identity to access all features
          </p>
        </div>

        {/* Transaction Limits Overview */}
        {!isLoadingLimits && transactionLimits && (
          <div className={`rounded-xl p-6 ${
            kycStatus === 'VERIFIED'
              ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20'
              : 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20'
          }`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">
                  {kycStatus === 'VERIFIED' ? 'KYC Verified Account' : 'Non-KYC Account'}
                </h3>
                <p className="text-sm text-slate-400">
                  {kycStatus === 'VERIFIED'
                    ? 'You have full access to all transaction limits'
                    : 'Complete KYC verification below to unlock higher limits'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-black/20 rounded-lg p-4">
                <div className="text-xs text-slate-400 mb-1">Single Transaction</div>
                <div className="text-2xl font-bold text-white">
                  ${transactionLimits.limits.single.toLocaleString()}
                </div>
                {kycStatus !== 'VERIFIED' && (
                  <div className="text-xs text-green-400 mt-2">
                    ↑ $50,000 with KYC
                  </div>
                )}
              </div>

              <div className="bg-black/20 rounded-lg p-4">
                <div className="text-xs text-slate-400 mb-1">Daily Limit</div>
                <div className="text-2xl font-bold text-white">
                  ${transactionLimits.remaining.daily.toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  of ${transactionLimits.limits.daily.toLocaleString()} remaining
                </div>
                {kycStatus !== 'VERIFIED' && (
                  <div className="text-xs text-green-400 mt-1">
                    ↑ $200,000 with KYC
                  </div>
                )}
              </div>

              <div className="bg-black/20 rounded-lg p-4">
                <div className="text-xs text-slate-400 mb-1">
                  {transactionLimits.limits.monthly ? 'Monthly Limit' : 'Monthly'}
                </div>
                <div className="text-2xl font-bold text-white">
                  {transactionLimits.limits.monthly
                    ? `$${transactionLimits.remaining.monthly.toLocaleString()}`
                    : 'Unlimited'}
                </div>
                {transactionLimits.limits.monthly && (
                  <div className="text-xs text-slate-500 mt-1">
                    of ${transactionLimits.limits.monthly.toLocaleString()} remaining
                  </div>
                )}
                {kycStatus !== 'VERIFIED' && transactionLimits.limits.monthly && (
                  <div className="text-xs text-green-400 mt-1">
                    ↑ Unlimited with KYC
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* KYC Error Alert from Widget */}
        {errorType === 'kyc' && (
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="text-yellow-400 text-xl">⚠️</div>
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-400">KYC Details Need Update</h3>
                <p className="text-sm text-slate-300 mt-1">
                  Your transaction could not be processed due to issues with your KYC information.
                  {errorFields.length > 0 && (
                    <span> Please update the following fields: <strong>{errorFields.map(f => getFieldLabel(f)).join(', ')}</strong></span>
                  )}
                </p>
                {sessionId && (
                  <p className="text-xs text-slate-400 mt-2">Session ID: {sessionId}</p>
                )}
              </div>
              <button
                onClick={() => setSearchParams({})}
                className="text-yellow-400 hover:text-yellow-300"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {submitSuccess && (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="text-green-400 text-xl">✓</div>
              <p className="text-sm text-slate-300">
                KYC details updated successfully! Your information has been submitted for verification.
              </p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-[var(--space-l)]">
              <div className="w-12 h-12 rounded-full border-4 border-purple-500 border-t-transparent animate-spin" />
              <p className="text-slate-400 text-[var(--font-size-body)]">Loading KYC information...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="swap-card rounded-2xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-white">Verification Status</h2>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(status?.status)}`}>
                  {status?.status || 'NOT_STARTED'}
                </span>
              </div>

              <p className="text-slate-400 mb-6">{getStatusMessage(status?.status)}</p>

              {status?.status === 'VERIFIED' && status?.verifiedAt && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl mb-4">
                  <p className="text-sm text-green-400 font-medium">
                    Verified on {new Date(status.verifiedAt).toLocaleDateString()}
                  </p>
                  {status.expiresAt && (
                    <p className="text-sm text-slate-300 mt-1">
                      Expires on {new Date(status.expiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {status?.status === 'FAILED' && status?.failureReason && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-4">
                  <p className="text-sm font-medium text-red-400">Reason:</p>
                  <p className="text-sm text-slate-300 mt-1">{status.failureReason}</p>
                </div>
              )}

              {/* Edit KYC Button for Verified Users */}
              {status?.status === 'VERIFIED' && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20"
                >
                  <span className="relative z-10">Update KYC Details</span>
                </button>
              )}

              {/* Start Verification Button */}
              {(!status?.status || status?.status === 'NOT_STARTED' || status?.status === 'FAILED' || status?.status === 'EXPIRED') && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20"
                >
                  <span className="relative z-10">
                    {status?.status === 'FAILED' || status?.status === 'EXPIRED' ? 'Retry Verification' : 'Start Verification'}
                  </span>
                </button>
              )}
            </div>

            {/* KYC Form */}
            {isEditing && requirements && (
              <div className="swap-card rounded-2xl p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
                  {status?.status === 'VERIFIED' ? 'Update' : 'Enter'} Your KYC Details
                </h2>

                {submitError && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-4">
                    <p className="text-sm text-red-400">{submitError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {requirements.fields?.map((field: any) => (
                    <div key={field.documentType}>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        {getFieldLabel(field.documentType)}
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                      <input
                        type={getFieldType(field.documentType)}
                        value={formData[field.documentType] || ''}
                        onChange={(e) => handleChange(field.documentType, e.target.value)}
                        placeholder={getFieldPlaceholder(field.documentType)}
                        required={field.required}
                        className="w-full px-4 py-3 bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      />
                    </div>
                  ))}

                  <div className="flex gap-3 mt-8">
                    <button
                      type="submit"
                      disabled={submitMutation.isPending}
                      className="swap-button flex-1 py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="relative z-10">
                        {submitMutation.isPending ? 'Submitting...' : 'Submit KYC Details'}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setSubmitError(null);
                      }}
                      className="px-6 py-4 border-2 border-slate-700 rounded-2xl text-white font-semibold hover:bg-slate-800 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!isEditing && (
              <>
                <div className="swap-card rounded-2xl p-6 md:p-8">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-6">Why Verify?</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Higher Transaction Limits</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Verified users can process larger transactions
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Faster Processing</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Your transactions are processed with priority
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Enhanced Security</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Protect your account with verified identity
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Access All Features</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Unlock all platform features and payment methods
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="swap-card rounded-2xl p-6 md:p-8">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-6">What You'll Need</h2>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start gap-3">
                      <span className="text-purple-400 mt-0.5">•</span>
                      <span>Valid government-issued ID (Passport, Driver's License, or National ID)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-purple-400 mt-0.5">•</span>
                      <span>Proof of address (Utility bill, Bank statement, etc.)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-purple-400 mt-0.5">•</span>
                      <span>Clear selfie photo</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-purple-400 mt-0.5">•</span>
                      <span>5-10 minutes to complete the process</span>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
